import { Wheel, WheelProps, ItemProps } from 'spin-wheel';
import { GameRenderer, GameResult } from '../../interfaces/game-type-plugin.interface';
import { WheelGameSettings, WHEEL_SIZE_PIXELS } from '@kanek/shared-data-access';
import { WheelSceneComponent } from '../../scenes/wheel-scene.component';
import { WheelConfigMapper } from './wheel-config-mapper';

import { MockPrizeApiService } from '@kanek/shared-data-access';

/**
 * Configuration object for WheelRenderer.updateConfig()
 */
export interface WheelRendererConfig {
  settings: WheelGameSettings | WheelProps;
  items: ItemProps[] | Array<{ label?: string; labelColor?: string; backgroundColor?: string; [key: string]: unknown }>;
}

/**
 * Wheel game renderer using the spin-wheel library.
 * Handles wheel creation, configuration updates, spinning, and cleanup.
 *
 * HYBRID RENDERING:
 * - HTML: Uses spin-wheel.js library for the wheel face
 * - 3D: Creates cylinder base and arrow pointer using Three.js
 */
export class WheelRenderer implements GameRenderer<WheelRendererConfig> {
  private wheel: Wheel | null = null;
  private container: HTMLElement | null = null;
  private currentSettings: WheelGameSettings | null = null;
  private items: any[] = [];

  // Optional event callbacks (can be set by consumers)
  public onSpin?: () => void;
  public onRest?: (currentIndex: number) => void;
  public onCurrentIndexChange?: (currentIndex: number) => void;

  // Canvas store for accessing scene.userData (sceneGraph mode)
  private canvasStore: any | null = null;

  // ArService for keepVisible flag during critical moments
  private arService?: any;

  setCanvasStore(store: any): void {
    this.canvasStore = store;
  }

  /**
   * Set AR service for keepVisible flag during spin
   */
  setArService(arService: any): void {
    this.arService = arService;
  }

  /**
   * @param container - HTML element to render wheel
   * @param arService - Optional AR service for keepVisible flag during spin
   */
  initialize(container: HTMLElement, arService?: any): void {
    this.container = container;
    this.arService = arService;

    // Set container styles for proper rendering
    // Note: Dimensions will be set by updateConfig based on settings
    container.style.position = 'relative';
  }

  async updateConfig(config: WheelRendererConfig): Promise<void> {
    this.currentSettings = config.settings as WheelGameSettings;
    this.items = (config.items || []) as any[];

    // Apply container dimensions from settings
    // For WheelGameSettings, use wheelSize preset
    // For WheelProps (Experience app), dimensions aren't in the props, so default to medium (768px)
    if (this.container) {
      if (this.isWheelProps(config.settings)) {
        // WheelProps format - use default medium size
        const size = WHEEL_SIZE_PIXELS.medium;
        this.container.style.width = `${size}px`;
        this.container.style.height = `${size}px`;
      } else {
        // WheelGameSettings format - use preset size
        const settings = config.settings as WheelGameSettings;
        const size = WHEEL_SIZE_PIXELS[settings.wheelSize || 'medium'];
        this.container.style.width = `${size}px`;
        this.container.style.height = `${size}px`;
      }
    }

    // Convert settings to WheelProps with async image loading
    // Handle both WheelGameSettings and WheelProps input
    let wheelProps: WheelProps;

    if (this.isWheelProps(config.settings)) {
      // Already in WheelProps format (from Experience App)
      wheelProps = config.settings;
    } else {
      // Convert from WheelGameSettings (from Portal)
      wheelProps = await WheelConfigMapper.toWheelPropsAsync(config.settings, this.items);
    }

    if (this.wheel) {
      // Update existing wheel
      this.wheel.init({
        ...wheelProps,
      });
    } else if (this.container) {
      // Create new wheel
      this.wheel = new Wheel(this.container, wheelProps);

      // Wire up event callbacks
      this.wheel.onSpin = () => {
        this.onSpin?.();
      };

      this.wheel.onRest = (event) => {
        this.onRest?.(event.currentIndex);
      };

      this.wheel.onCurrentIndexChange = (event) => {
        this.onCurrentIndexChange?.(event.currentIndex);
      };
    }
  }

  /**
   * Start the wheel game by spinning to a winning prize.
   *
   * Platform-agnostic design:
   * - Accepts getPrizeIndex callback from platform (Experience/Portal)
   * - Calls callback to get winning index (handles Prize API, mock, etc.)
   * - Spins wheel to the returned prize index
   *
   * @param options - Play options
   * @param options.getPrizeIndex - Platform callback to get winning prize index
   * @param options.winningIndex - Pre-determined index (legacy/override)
   * @returns Promise resolving with game result when wheel stops
   */
  async play(options?: { getPrizeIndex?: () => Promise<number>; winningIndex?: number }): Promise<GameResult> {
    // In sceneGraph mode, wheel is created by WheelSceneComponent and stored in scene.userData
    // In container mode, wheel is created by this renderer in updateConfig()
    let wheel = this.wheel;
    let isSceneGraphMode = false;

    // If no wheel in renderer, try to get from scene.userData (sceneGraph mode)
    if (!wheel && this.canvasStore?.scene) {
      wheel = this.canvasStore.scene.userData?.['wheelInstance'];
      isSceneGraphMode = !!wheel;

      // Wire up callbacks for sceneGraph mode (they're only wired in updateConfig for container mode)
      if (wheel) {
        wheel.onSpin = () => {
          this.onSpin?.();
        };

        wheel.onRest = (event: any) => {
          this.onRest?.(event.currentIndex);
        };

        // Preserve the original onCurrentIndexChange callback (e.g., tick sound)
        const originalOnCurrentIndexChange = wheel.onCurrentIndexChange;
        wheel.onCurrentIndexChange = (event: any) => {
          originalOnCurrentIndexChange?.(event); // Call original (tick sound)
          this.onCurrentIndexChange?.(event.currentIndex); // Call renderer wrapper (game-engine)
        };
      }
    }

    if (!wheel) {
      throw new Error('Wheel not initialized');
    }

    // Determine winning index using platform callback or fallback
    let winningIndex: number;

    if (options?.getPrizeIndex) {
      // Keep scene visible from the moment user commits to spin
      // Scene stays visible through wind-up, Prize API call, and final spin
      if (this.arService) {
        this.arService.keepVisible = true;
      }

      // Wind-up animation: small backwards spin for immediate user feedback
      // This plays while waiting for Prize API response
      // Temporarily disable onRest to prevent premature completion during wind-up
      const savedOnRest = wheel.onRest;
      wheel.onRest = null as any;

      wheel.spin(-10);

      winningIndex = await options.getPrizeIndex();

      // Stop wind-up animation before starting winning spin
      wheel.stop();

      // Restore onRest handler for actual winning spin
      wheel.onRest = savedOnRest;
    } else if (options?.winningIndex !== undefined) {
      // Legacy: Pre-determined winning index
      winningIndex = options.winningIndex;
    } else if (this.items && this.items.length > 0) {
      // Fallback: Random selection if no callback provided
      winningIndex = Math.floor(Math.random() * this.items.length);
    } else {
      throw new Error('[WheelRenderer] No getPrizeIndex callback, winningIndex, or items available');
    }

    // Spin to the predetermined winning prize
    // Parameters match Experience app for consistent behavior:
    // - duration: 5000ms (5 seconds)
    // - spinToCenter: false
    // - numberOfRevolutions: 4
    // - direction: 1 (clockwise)
    // - easingFunction: easeOutBounce from 'easing-utils'
    // Note: In production, winningIndex comes from Prize API
    const { easeOutBounce } = await import('easing-utils');
    wheel.spinToItem(winningIndex, 5000, false, 4, 1, easeOutBounce);

    // Wait for wheel to stop
    return new Promise((resolve) => {
      const originalOnRest = wheel.onRest;

      wheel.onRest = (event) => {
        // Call original handler
        if (originalOnRest) {
          originalOnRest(event);
        }

        // Resolve with result
        const winningItem = this.items[event.currentIndex];
        const isWinner = !!winningItem?.label;

        // Trigger confetti celebration for wins
        if (isWinner && this.canvasStore) {
          const scene = this.canvasStore.scene;
          const confettiService = scene?.userData?.['confettiService'];
          if (confettiService) {
            confettiService.win();
          }

          // Play win sound
          this.playSound('assets/sfx/win.mp3');

          // Set prizeRevealed flag after a brief delay to show celebration
          // This enables event-driven navigation (same pattern as scratchcard)
          setTimeout(() => {
            const appStore = scene?.userData?.['appStore'];
            if (appStore) {
              const session = appStore.session();
              appStore.setSession({
                ...session,
                prizeRevealed: true
              });
            }
          }, 2000); // 2 second delay for celebration visibility
        }

        resolve({
          isWinner,
          prize: isWinner ? {
            id: String(event.currentIndex),
            name: String(winningItem.label || ''),
            type: 'unknown',
          } : undefined,
          metadata: {
            currentIndex: event.currentIndex,
            rotation: event.rotation,
          },
        });
      };
    });
  }

  /**
   * Get the Angular Three scene component for declarative rendering.
   *
   * **Phase D Architecture**:
   * Returns `WheelSceneComponent` - a declarative Angular component that uses
   * Angular Three (ngt-mesh, ngts-html, etc.) for reactive 3D rendering.
   *
   * Benefits:
   * - ✅ Automatic updates via Angular signals (no manual scene manipulation)
   * - ✅ Type-safe props with TypeScript
   * - ✅ Easier testing and debugging
   * - ✅ Follows Angular 19+ patterns
   *
   * Usage:
   * ```typescript
   * const SceneComponent = renderer.getSceneComponent();
   * // In template: <ngt-canvas [sceneGraph]="SceneComponent" />
   * ```
   *
   * @returns WheelSceneComponent class for declarative rendering
   * @see WheelSceneComponent for implementation details
   */
  getSceneComponent() {
    return WheelSceneComponent;
  }

  destroy(): void {
    // Cleanup HTML wheel
    if (this.wheel) {
      this.wheel.remove();
      this.wheel = null;
    }

    this.container = null;
    this.currentSettings = null;
    this.items = [];
  }

  /**
   * Reset game state so the wheel can be spun again.
   *
   * Cancels any in-progress spin animation. The wheel face stays at its current
   * visual position — the next call to `play()` will spin from wherever it is,
   * which is identical to production behaviour.
   *
   * Preview-only: called from GameEngineComponent.resetGame() guarded by previewMode.
   */
  async reset(): Promise<void> {
    // Stop in-progress spin in container mode
    if (this.wheel) {
      this.wheel.stop();
      return;
    }

    // Stop in-progress spin in sceneGraph mode (wheel lives in scene.userData)
    if (this.canvasStore?.scene) {
      const wheel = this.canvasStore.scene.userData?.['wheelInstance'] as any;
      if (wheel && typeof wheel.stop === 'function') {
        wheel.stop();
      }
    }
  }

  async getScreenshot(): Promise<string> {
    if (!this.container) {
      throw new Error('Container not initialized');
    }

    // TODO: Implement screenshot using html2canvas when available
    // For now, return a placeholder
    return Promise.resolve('data:image/png;base64,placeholder');
  }

  /**
   * Type guard to check if settings are already in WheelProps format
   */
  private isWheelProps(settings: WheelGameSettings | WheelProps): settings is WheelProps {
    // WheelProps has 'items' array at the root level, WheelGameSettings doesn't
    return 'items' in settings && Array.isArray((settings as WheelProps).items);
  }

  /**
   * Play sound effect
   */
  private playSound(src: string): void {
    if (this.canvasStore) {
      const scene = this.canvasStore.scene;
      const audioService = scene?.userData?.['audioService'];
      if (audioService) {
        audioService.play(src, { volume: 0.7 });
      }
    }
  }
}
